import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { useSearchParams } from "react-router";
import { useQuery, useLazyQuery, useMutation } from "@apollo/client";
import { Archive, ArchiveRestore, Check, Download, Loader2, Minus, Plus, Users, X } from "lucide-react";
import {
  Avatar,
  Badge,
  EmptyState,
  PageHeader,
  Pagination,
  TagChips,
  Tooltip,
} from "../components/ui";
import { useToastPush } from "../components/ui/Toast";
import {
  GET_CLIENTS,
  GET_CLIENT_TAB_COUNTS,
  GET_ALL_PERSON_TAGS,
  EXPORT_ALL_CLIENTS,
  ARCHIVE_CLIENTS,
} from "../apollo/clients";
import { ClientDetailPane } from "./clients/ClientDetailPane";
import { AddClientModal } from "./clients/AddClientModal";
import { ClientOmnibox } from "./clients/ClientOmnibox";
import {
  deriveStatus,
  clientTags,
  primaryContact,
  getDisplayName,
  getSecondaryName,
  getInitials,
  formatSessionLabel,
  formatCurrency,
  CLIENT_STATUS_LABEL,
  CLIENT_STATUS_COLOR,
} from "../data/clientDerive";
import type { ListClient, ClientStatus } from "../data/clientDerive";
import type { HeaderTab } from "../components/ui";

// ── Constants ──────────────────────────────────────────────────────

const PER_PAGE = 50;
const DEBOUNCE_MS = 300;

function ClientStatusPill({ status }: { status: ClientStatus }) {
  const cfg = CLIENT_STATUS_COLOR[status];
  return (
    <div
      className={`inline-flex items-center gap-1.5 text-[11.5px] font-medium ${cfg.text}`}
    >
      <div className={`dot ${cfg.dot}`} />
      {CLIENT_STATUS_LABEL[status]}
    </div>
  );
}

// ── Sort options ────────────────────────────────────────────────────

type SortKey = "name" | "added" | "lastActive";

const SORT_OPTIONS: { key: SortKey; label: string; field: string; order: string }[] = [
  { key: "name", label: "Name A-Z", field: "name", order: "ASC" },
  { key: "added", label: "Date added", field: "createdAt", order: "DESC" },
  { key: "lastActive", label: "Last active", field: "updatedAt", order: "DESC" },
];

// ── Filter tabs ─────────────────────────────────────────────────────

// "prospects" surfaces never-booked Clients — funnel drop-offs backfilled into
// the roster by #31 (#476). Hidden from every other tab by default; reachable
// here deliberately.
type FilterKey = "all" | "new" | "prospects" | "archived";

// ── CSV export ──────────────────────────────────────────────────────

function buildCsv(clients: ListClient[]): string {
  const header =
    "Name,People,Person Emails,Status,Sessions,Lifetime Spend,Last Session,Next Session";
  const lines = clients.map((c) => {
    const escape = (v: string | null | undefined) => {
      if (!v) return "";
      if (v.includes(",") || v.includes('"')) {
        return `"${v.replace(/"/g, '""')}"`;
      }
      return v;
    };
    const status = deriveStatus(c);
    const lastSession = c.lastSessionAt
      ? formatSessionLabel(new Date(c.lastSessionAt))
      : "";
    const nextSession = c.nextSessionAt
      ? formatSessionLabel(new Date(c.nextSessionAt))
      : "";
    return [
      escape(getDisplayName(c)),
      c.peopleCount,
      escape((c.people ?? []).map((p) => p.email).join("; ")),
      status,
      c.sessionsCount,
      c.lifetimeSpend,
      escape(lastSession),
      escape(nextSession),
    ].join(",");
  });

  return [header, ...lines].join("\n");
}

function downloadCsv(csv: string, filename?: string) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename ?? `clients-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── Component ───────────────────────────────────────────────────────

// ── URL param helpers ───────────────────────────────────────────────

const DEFAULT_SORT: SortKey = "added";
const DEFAULT_FILTER: FilterKey = "all";

function readSort(params: URLSearchParams): SortKey {
  const v = params.get("sort");
  if (v === "name" || v === "added" || v === "lastActive") return v;
  return DEFAULT_SORT;
}

function readFilter(params: URLSearchParams): FilterKey {
  const v = params.get("filter");
  if (v === "all" || v === "new" || v === "prospects" || v === "archived") return v;
  return DEFAULT_FILTER;
}

function readPage(params: URLSearchParams): number {
  const v = params.get("page");
  if (v != null) {
    const n = parseInt(v, 10);
    if (!Number.isNaN(n) && n >= 0) return n;
  }
  return 0;
}

/**
 * The API filter for one tab. Search and tags are shared across every tab, so
 * the header's per-tab counts reuse this with only `filterKey` varying.
 */
function buildFilter(
  filterKey: FilterKey,
  search: string,
  tags: string[],
): Record<string, unknown> | undefined {
  const f: Record<string, unknown> = {};
  if (search) {
    f.q = search;
  }
  // Archive filter: "all"/"new"/"prospects" = hide archived, "archived" = show only archived
  if (filterKey === "archived") {
    f.archived = true;
  } else {
    f.archived = false;
  }
  // Prospect filter (#476): a prospect is a Client with no booking yet.
  // "prospects" shows only them; every other active tab hides them so
  // funnel drop-offs stay out of the book of business by default.
  if (filterKey === "prospects") {
    f.prospect = true;
  } else if (filterKey !== "archived") {
    f.prospect = false;
  }
  // "New" tab: only clients created in the last 30 days
  if (filterKey === "new") {
    const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    f.createdAfter = thirtyDaysAgo.toISOString();
  }
  // Tag filter: only clients with ALL selected tags
  if (tags.length > 0) {
    f.tagSlugs = tags;
  }
  return Object.keys(f).length > 0 ? f : undefined;
}

function readTags(params: URLSearchParams): string[] {
  const v = params.get("tags");
  if (!v) return [];
  return v.split(",").map((s) => s.trim()).filter(Boolean);
}

export function Clients() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("selected");
  const toast = useToastPush();

  // ── Derive list state from URL search params ────────────────────
  const sortKey = readSort(searchParams);
  const filterKey = readFilter(searchParams);
  const page = readPage(searchParams);
  const debouncedSearch = searchParams.get("q") ?? "";

  const selectedTags = readTags(searchParams);

  // searchInput is local state — it's the live typing buffer. On mount,
  // seed it from the URL's "q" param so a restored URL shows the query.
  const [searchInput, setSearchInput] = useState(() => searchParams.get("q") ?? "");
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Auto-open Add Client modal when linked from ManualSessionModal (?new=1)
  useEffect(() => {
    if (searchParams.get("new") === "1") setAddModalOpen(true);
  }, [searchParams]);

  // ── Bulk selection state (component state, not URL) ─────────────
  const [checkedIds, setCheckedIds] = useState<Set<string>>(new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  // Archive confirmation dialog
  const [archiveConfirm, setArchiveConfirm] = useState<{
    action: "archive" | "unarchive";
    count: number;
  } | null>(null);

  const detailPaneRef = useRef<HTMLElement>(null);

  // React Router recreates `setSearchParams` on every URL change (its
  // useCallback closes over the current searchParams). Keep the latest in a
  // ref so effects and callbacks below can write to URL without listing
  // setSearchParams as a dependency — this is critical to prevent the
  // selection-clearing bug where updating the URL would cascade through
  // effects that depend on the identity of setSearchParams.
  const setSearchParamsRef = useRef(setSearchParams);
  useEffect(() => {
    setSearchParamsRef.current = setSearchParams;
  });

  // ── URL writers (all use ref to avoid effect re-fires) ──────────

  const setSortKey = useCallback((key: SortKey) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      if (key === DEFAULT_SORT) next.delete("sort"); else next.set("sort", key);
      next.delete("page"); // reset to first page
      return next;
    });
  }, []);

  const setPage = useCallback((p: number) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      if (p === 0) next.delete("page"); else next.set("page", String(p));
      return next;
    });
  }, []);

  const addTag = useCallback((slug: string) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      const current = readTags(prev);
      if (current.includes(slug)) return prev; // already selected
      const updated = [...current, slug];
      next.set("tags", updated.join(","));
      next.delete("page");
      return next;
    });
  }, []);

  const removeTag = useCallback((slug: string) => {
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      const current = readTags(prev);
      const updated = current.filter((s) => s !== slug);
      if (updated.length === 0) next.delete("tags"); else next.set("tags", updated.join(","));
      next.delete("page");
      return next;
    });
  }, []);

  // Clear the omnibox: wipe the search text and tag filters (resets to page 1).
  // Status tab and sort are separate controls and are left untouched.
  const clearOmni = useCallback(() => {
    setSearchInput("");
    setSearchParamsRef.current((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("q");
      next.delete("tags");
      next.delete("page");
      return next;
    });
  }, []);

  // Tag vocabulary for the filter chips
  const { data: tagsVocabData } = useQuery(GET_ALL_PERSON_TAGS, {
    fetchPolicy: "cache-and-network",
  });
  const tagVocabulary: { id: string; name: string; slug: string }[] =
    tagsVocabData?.allPersonTags ?? [];

  // Debounce search input: write "q" to URL after DEBOUNCE_MS.
  // Also resets page to 0 when the query changes.
  // Skip the URL write when the trimmed input already matches the current
  // URL value — avoids resetting ?page= on mount when restoring a URL.
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      setSearchParamsRef.current((prev) => {
        const currentQ = prev.get("q") ?? "";
        if (trimmed === currentQ) return prev; // no change — skip
        const next = new URLSearchParams(prev);
        if (trimmed) next.set("q", trimmed); else next.delete("q");
        next.delete("page"); // reset to first page on search change
        return next;
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Clear any open selection AND bulk selection when the visible result set
  // changes (filter, sort, or search). Skip the initial mount so a deep link
  // to ?selected=<id> survives a page refresh.
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    // Clear bulk selection
    setCheckedIds(new Set());
    setSelectAllMatching(false);
    // Clear detail-pane selection
    setSearchParamsRef.current((prev) => {
      if (!prev.has("selected")) return prev;
      const next = new URLSearchParams(prev);
      next.delete("selected");
      return next;
    });
  }, [filterKey, sortKey, debouncedSearch, selectedTags.join(",")]);

  // Build query variables
  const sortOption = SORT_OPTIONS.find((s) => s.key === sortKey) ?? SORT_OPTIONS[1];

  // Stable string for the tags dependency (avoids object identity issues)
  const tagsKey = selectedTags.join(",");

  const filter = useMemo(
    () => buildFilter(filterKey, debouncedSearch, selectedTags),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearch, filterKey, tagsKey],
  );

  const { data, loading, error, previousData, refetch } = useQuery(GET_CLIENTS, {
    variables: {
      page,
      perPage: PER_PAGE,
      sortField: sortOption.field,
      sortOrder: sortOption.order,
      filter,
    },
    fetchPolicy: "cache-and-network",
  });

  // Only fall back to previousData when paginating within the same
  // filter/search/sort context. When those variables change, show
  // the skeleton instead of stale rows from the old context.
  const variablesKey = `${debouncedSearch}|${filterKey}|${sortOption.field}|${sortOption.order}|${tagsKey}`;
  const prevKeyRef = useRef(variablesKey);
  const contextChanged = variablesKey !== prevKeyRef.current;
  useEffect(() => {
    if (data) {
      prevKeyRef.current = variablesKey;
    }
  }, [data, variablesKey]);

  const effectiveData = data ?? (contextChanged ? undefined : previousData);
  const isStale = loading && effectiveData != null && data == null;
  const clients: ListClient[] = effectiveData?.allClients ?? [];
  const totalCount: number = effectiveData?._allClientsMeta?.count ?? 0;

  // ── Export-all (lazy query) ─────────────────────────────────────
  const [fetchExportAll, { loading: exportAllLoading }] = useLazyQuery(
    EXPORT_ALL_CLIENTS,
    { fetchPolicy: "network-only" },
  );

  const handleExportAll = useCallback(async () => {
    try {
      const { data: exportData } = await fetchExportAll({
        variables: {
          sortField: sortOption.field,
          sortOrder: sortOption.order,
          filter,
        },
      });
      const allClients: ListClient[] =
        exportData?.exportAllClients ?? [];
      if (allClients.length === 0) {
        toast({ kind: "info", title: "Nothing to export" });
        return;
      }
      downloadCsv(buildCsv(allClients));
      toast({
        kind: "success",
        title: `Exported ${allClients.length} ${allClients.length === 1 ? "client" : "clients"}`,
      });
    } catch {
      toast({
        kind: "error",
        title: "Export failed",
        description: "Something went wrong. Please try again.",
      });
    }
  }, [fetchExportAll, sortOption, filter, toast]);

  // ── Bulk archive mutation ───────────────────────────────────────
  const [archiveClientsMut, { loading: archiveLoading }] = useMutation(ARCHIVE_CLIENTS);

  // Build display rows with derived fields
  const rows = useMemo(
    () =>
      clients.map((c) => ({
        client: c,
        status: deriveStatus(c),
        displayName: getDisplayName(c),
        secondaryName: getSecondaryName(c),
        initials: getInitials(c),
        contact: primaryContact(c),
        tags: clientTags(c),
        isNew:
          !c.archivedAt &&
          new Date(c.createdAt).getTime() >
            Date.now() - 30 * 24 * 60 * 60 * 1000,
      })),
    [clients],
  );

  type ClientRow = (typeof rows)[number];

  // ── Bulk selection helpers ──────────────────────────────────────

  // The effective count of selected items. When selectAllMatching is
  // true, we operate on the full totalCount (server will receive all
  // matching IDs via the export-all query).
  const selectionCount = selectAllMatching ? totalCount : checkedIds.size;
  const hasSelection = selectionCount > 0;

  // Page-level IDs for select-all-on-page
  const pageIds = useMemo(
    () => new Set(clients.map((c) => c.id)),
    [clients],
  );

  const allOnPageChecked =
    pageIds.size > 0 && Array.from(pageIds).every((id) => checkedIds.has(id));
  const someOnPageChecked =
    !allOnPageChecked &&
    pageIds.size > 0 &&
    Array.from(pageIds).some((id) => checkedIds.has(id));

  // Anchor for shift-click range selection: the id of the last row toggled
  // with a plain (non-shift) click. Stored as an id (not an index) so it
  // survives re-renders; if it's not on the current page, range-select
  // falls back to a single toggle.
  const checkAnchorRef = useRef<string | null>(null);

  const handleRowCheck = useCallback(
    (id: string, shiftKey: boolean) => {
      const anchorId = checkAnchorRef.current;
      if (shiftKey && anchorId && anchorId !== id) {
        const anchorIdx = rows.findIndex((r) => r.client.id === anchorId);
        const clickedIdx = rows.findIndex((r) => r.client.id === id);
        if (anchorIdx !== -1 && clickedIdx !== -1) {
          const [lo, hi] =
            anchorIdx < clickedIdx ? [anchorIdx, clickedIdx] : [clickedIdx, anchorIdx];
          const rangeIds = rows.slice(lo, hi + 1).map((r) => r.client.id);
          setSelectAllMatching(false);
          setCheckedIds((prev) => {
            const next = new Set(prev);
            for (const rid of rangeIds) next.add(rid);
            return next;
          });
          // Keep the anchor put so the range can be re-adjusted with another
          // shift-click, matching the familiar file-list / Gmail behavior.
          return;
        }
      }
      // Plain click: toggle this row and make it the new range anchor.
      setSelectAllMatching(false);
      setCheckedIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) {
          next.delete(id);
        } else {
          next.add(id);
        }
        return next;
      });
      checkAnchorRef.current = id;
    },
    [rows],
  );

  const toggleAllOnPage = useCallback(() => {
    setSelectAllMatching(false);
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageChecked) {
        // Uncheck all on this page
        for (const id of pageIds) {
          next.delete(id);
        }
      } else {
        // Check all on this page
        for (const id of pageIds) {
          next.add(id);
        }
      }
      return next;
    });
  }, [allOnPageChecked, pageIds]);

  const clearBulkSelection = useCallback(() => {
    setCheckedIds(new Set());
    setSelectAllMatching(false);
    checkAnchorRef.current = null;
  }, []);

  // ── Bulk action handlers ────────────────────────────────────────

  /** Resolve the IDs that should be acted on. For select-all-matching,
   *  fetch all matching IDs via the export-all query. */
  const resolveSelectedIds = useCallback(async (): Promise<string[]> => {
    if (!selectAllMatching) {
      return Array.from(checkedIds);
    }
    // Fetch all matching IDs
    const { data: exportData } = await fetchExportAll({
      variables: {
        sortField: sortOption.field,
        sortOrder: sortOption.order,
        filter,
      },
    });
    const all: ListClient[] = exportData?.exportAllClients ?? [];
    return all.map((c) => c.id);
  }, [selectAllMatching, checkedIds, fetchExportAll, sortOption, filter]);

  const handleBulkArchive = useCallback(
    async (archive: boolean) => {
      setArchiveConfirm(null);
      try {
        const ids = await resolveSelectedIds();
        if (ids.length === 0) return;

        await archiveClientsMut({
          variables: { ids, archive },
        });

        await refetch();
        clearBulkSelection();
        const verb = archive ? "archived" : "unarchived";
        toast({
          kind: "success",
          title: `${ids.length} ${ids.length === 1 ? "client" : "clients"} ${verb}`,
        });
      } catch {
        toast({
          kind: "error",
          title: `Failed to ${archive ? "archive" : "unarchive"} clients`,
          description: "Something went wrong. Please try again.",
        });
      }
    },
    [resolveSelectedIds, archiveClientsMut, refetch, clearBulkSelection, toast],
  );

  const handleExportSelected = useCallback(async () => {
    try {
      // Checked ids deliberately survive pagination, so the export must NOT
      // be built from the visible page — fetch the full payload for either
      // the current filters (select-all-matching) or the exact checked ids.
      const { data: exportData } = await fetchExportAll({
        variables: {
          sortField: sortOption.field,
          sortOrder: sortOption.order,
          filter: selectAllMatching ? filter : { ids: Array.from(checkedIds) },
        },
      });
      const toExport: ListClient[] = exportData?.exportAllClients ?? [];
      if (toExport.length === 0) {
        toast({ kind: "info", title: "Nothing to export" });
        return;
      }
      downloadCsv(buildCsv(toExport), `clients-selected-${new Date().toISOString().slice(0, 10)}.csv`);
      toast({
        kind: "success",
        title: `Exported ${toExport.length} ${toExport.length === 1 ? "client" : "clients"}`,
      });
    } catch {
      toast({
        kind: "error",
        title: "Export failed",
        description: "Something went wrong. Please try again.",
      });
    }
  }, [selectAllMatching, fetchExportAll, sortOption, filter, checkedIds, toast]);

  // Selection — preserve other search params (detail pane)
  const selectClient = useCallback(
    (row: ClientRow) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("selected", row.client.id);
        return next;
      });
    },
    [setSearchParams],
  );

  const clearSelection = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("selected");
      return next;
    });
  }, [setSearchParams]);

  // Escape-key handler to close detail pane
  useEffect(() => {
    if (!selectedId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        clearSelection();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [selectedId, clearSelection]);

  // Scroll detail pane into view on selection change
  useEffect(() => {
    if (selectedId && detailPaneRef.current) {
      detailPaneRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [selectedId]);

  // Per-tab counts. Four aliases of the existing meta field in one round trip,
  // each carrying the current search and tags so a tab's number matches what
  // clicking it returns.
  const { data: countsData } = useQuery(GET_CLIENT_TAB_COUNTS, {
    variables: {
      all: buildFilter("all", debouncedSearch, selectedTags),
      new: buildFilter("new", debouncedSearch, selectedTags),
      prospects: buildFilter("prospects", debouncedSearch, selectedTags),
      archived: buildFilter("archived", debouncedSearch, selectedTags),
    },
    fetchPolicy: "cache-and-network",
  });

  const tabCounts: Record<FilterKey, number | null> = {
    all: countsData?.all?.count ?? null,
    new: countsData?.new?.count ?? null,
    prospects: countsData?.prospects?.count ?? null,
    archived: countsData?.archived?.count ?? null,
  };

  // Summary text
  const summaryText = loading && clients.length === 0
    ? "Loading clients..."
    : totalCount > 0
      ? filterKey === "new"
        ? `${totalCount} new ${totalCount === 1 ? "client" : "clients"} in the last 30 days.`
        : filterKey === "prospects"
          ? `${totalCount} ${totalCount === 1 ? "prospect who hasn't" : "prospects who haven't"} booked yet.`
          : `${totalCount} ${totalCount === 1 ? "client" : "clients"} on the roster.`
      : filterKey === "archived"
        ? "No archived clients."
        : filterKey === "new"
          ? "No new clients in the last 30 days."
          : filterKey === "prospects"
            ? "No prospects — everyone on your roster has booked."
            : "No clients yet.";

  // Format session dates for display
  const formatDate = (iso: string | null) => {
    if (!iso) return null;
    const dt = new Date(iso);
    if (Number.isNaN(dt.getTime())) return null;
    return formatSessionLabel(dt);
  };

  // Row highlight for selected (detail pane)
  const rowHighlight = (r: ClientRow) =>
    r.client.id === selectedId
      ? "bg-tide-ss/60 hair-l border-l-[3px] !border-l-tide"
      : undefined;

  // Filter tab config
  // The default tab excludes prospects, so it is deliberately not "All" (that
  // would name a tab that isn't true) — it is the booked roster, and says so.
  const filterTabs: HeaderTab[] = [
    { key: "all", label: "Roster", count: tabCounts.all },
    { key: "new", label: "New (30 days)", count: tabCounts.new },
    { key: "prospects", label: "Prospects", count: tabCounts.prospects },
    { key: "archived", label: "Archived", count: tabCounts.archived },
  ];

  // Whether we're on the archived view (determines archive vs unarchive label)
  const isArchivedView = filterKey === "archived";

  // ── Checkbox column helpers ─────────────────────────────────────

  const CheckboxHeader = (
    <button
      type="button"
      onClick={toggleAllOnPage}
      className="w-4 h-4 rounded border hair flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer bg-paper-hi hover:bg-paper-2"
      aria-label={allOnPageChecked ? "Deselect all on this page" : "Select all on this page"}
    >
      {allOnPageChecked ? (
        <Check size={10} strokeWidth={3} className="text-tide" />
      ) : someOnPageChecked ? (
        <Minus size={10} strokeWidth={3} className="text-tide" />
      ) : null}
    </button>
  );

  return (
    <div>
      <PageHeader
        tabsLabel="Client views"
        tabs={filterTabs}
        activeTab={filterKey}
        tabParam="filter"
        defaultTab={DEFAULT_FILTER}
        status={summaryText}
        controls={
          <>
            <ClientOmnibox
              searchInput={searchInput}
              onSearchChange={setSearchInput}
              selectedTags={selectedTags}
              onTagAdd={addTag}
              onTagRemove={removeTag}
              onClear={clearOmni}
              vocabulary={tagVocabulary}
              placeholder={
                totalCount > 0
                  ? `Search ${totalCount} clients or filter by tag...`
                  : "Search clients or filter by tag..."
              }
            />
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as SortKey)}
              aria-label="Sort clients by"
              className="px-3 min-h-11 md:min-h-0 md:py-1.5 rounded-md hair border bg-paper-hi text-[12.5px] text-ink-3 hover:bg-paper-2 appearance-none cursor-pointer pr-7 flex-shrink-0"
              style={{
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%23516680' stroke-width='2' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 8px center",
              }}
            >
              {SORT_OPTIONS.map((s) => (
                <option key={s.key} value={s.key}>
                  {s.label}
                </option>
              ))}
            </select>
          </>
        }
        actions={
          <>
            <Tooltip label="Exports all clients matching the current filters">
              <button
                onClick={handleExportAll}
                disabled={totalCount === 0 || exportAllLoading}
                className="px-3.5 py-2 rounded-md bg-paper-hi text-ink-2 border hair hover:bg-paper-2 text-[13px] flex items-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {exportAllLoading ? (
                  <Loader2 size={13} strokeWidth={1.8} className="animate-spin" />
                ) : (
                  <Download size={13} strokeWidth={1.8} />
                )}
                {exportAllLoading ? "Exporting..." : "Export all (CSV)"}
              </button>
            </Tooltip>
            <button
              onClick={() => setAddModalOpen(true)}
              className="px-3.5 py-2 rounded-md bg-tide text-paper-hi text-[13px] font-medium flex items-center gap-1.5 hover:bg-tide-d transition-colors"
            >
              <Plus size={13} strokeWidth={2.2} />
              Add client
            </button>
          </>
        }
        mobileAction={{
          label: "Add client",
          icon: <Plus size={16} strokeWidth={2.2} />,
          onClick: () => setAddModalOpen(true),
        }}
      />

      {/* Selection toolbar */}
      {hasSelection && (
        <div className="mx-4 md:mx-7 mb-3 px-4 py-2.5 bg-tide-ss rounded-lg hair border border-tide/30 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 text-[13px]">
            <span className="font-medium text-ink">
              {selectionCount} {selectionCount === 1 ? "client" : "clients"} selected
            </span>
            {/* Show "Select all N matching" when the user selected the whole page
                but there are more pages, and they haven't already opted in. */}
            {allOnPageChecked && !selectAllMatching && totalCount > pageIds.size && (
              <button
                type="button"
                onClick={() => setSelectAllMatching(true)}
                className="text-tide hover:underline text-[12.5px]"
              >
                Select all {totalCount} matching
              </button>
            )}
            {selectAllMatching && (
              <span className="text-ink-3 text-[12.5px]">
                All {totalCount} matching clients are selected.
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleExportSelected}
              disabled={archiveLoading}
              className="px-3 py-1.5 rounded-md bg-paper-hi text-ink-2 border hair hover:bg-paper-2 text-[12.5px] flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              <Download size={12} strokeWidth={1.8} />
              Export selected
            </button>
            <button
              type="button"
              onClick={() =>
                setArchiveConfirm({
                  action: isArchivedView ? "unarchive" : "archive",
                  count: selectionCount,
                })
              }
              disabled={archiveLoading}
              className="px-3 py-1.5 rounded-md bg-paper-hi text-ink-2 border hair hover:bg-paper-2 text-[12.5px] flex items-center gap-1.5 transition-colors disabled:opacity-50"
            >
              {archiveLoading ? (
                <Loader2 size={12} strokeWidth={1.8} className="animate-spin" />
              ) : isArchivedView ? (
                <ArchiveRestore size={12} strokeWidth={1.8} />
              ) : (
                <Archive size={12} strokeWidth={1.8} />
              )}
              {isArchivedView ? "Unarchive" : "Archive"}
            </button>
            <button
              type="button"
              onClick={clearBulkSelection}
              className="w-6 h-6 rounded hover:bg-paper-2 flex items-center justify-center text-ink-4"
              aria-label="Clear selection"
            >
              <X size={14} strokeWidth={2} />
            </button>
          </div>
        </div>
      )}

      {/* Archive confirmation dialog */}
      {archiveConfirm && (
        <div className="mx-4 md:mx-7 mb-3 px-4 py-3 bg-paper-hi rounded-lg shadow-card hair border flex items-center justify-between gap-3">
          <div className="text-[13px] text-ink">
            {archiveConfirm.action === "archive" ? (
              <>
                Archive {archiveConfirm.count}{" "}
                {archiveConfirm.count === 1 ? "client" : "clients"}? They will
                be hidden from your active client list.
              </>
            ) : (
              <>
                Unarchive {archiveConfirm.count}{" "}
                {archiveConfirm.count === 1 ? "client" : "clients"}? They will
                reappear in your active client list.
              </>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setArchiveConfirm(null)}
              className="px-3 py-1.5 rounded-md text-ink-3 hover:bg-paper-2 text-[12.5px] transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => handleBulkArchive(archiveConfirm.action === "archive")}
              disabled={archiveLoading}
              className="px-3 py-1.5 rounded-md bg-ink text-paper-hi text-[12.5px] font-medium hover:bg-ink/90 transition-colors flex items-center gap-1.5 disabled:opacity-50"
            >
              {archiveLoading && (
                <Loader2 size={12} strokeWidth={2} className="animate-spin" />
              )}
              {archiveConfirm.action === "archive" ? "Archive" : "Unarchive"}
            </button>
          </div>
        </div>
      )}

      {/* Split: table + detail */}
      <div className="px-4 md:px-7 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-3">
        <div className={selectedId ? "lg:col-span-8" : "lg:col-span-12"}>
          {loading && rows.length === 0 ? (
            <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
              {/* Skeleton */}
              <div className="grid grid-cols-12 gap-3 px-5 py-2.5 hair-b bg-wash text-[11px] font-semibold text-ink-4 uppercase tracking-wider">
                <div className="col-span-1 flex items-center">
                  <div className="w-4 h-4 rounded bg-canvas animate-pulse" />
                </div>
                <div className="col-span-3">Name</div>
                <div className="col-span-2 hidden md:block">Status</div>
                <div className="col-span-2 hidden lg:block">Last session</div>
                <div className="col-span-2 hidden md:block">Lifetime</div>
                <div className="col-span-2 hidden lg:block">Next up</div>
              </div>
              <div className="divide-hair">
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    className="grid grid-cols-12 gap-3 px-5 py-4 items-center"
                  >
                    <div className="col-span-1 flex items-center">
                      <div className="w-4 h-4 rounded bg-canvas animate-pulse" />
                    </div>
                    <div className="col-span-3 flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-canvas animate-pulse flex-shrink-0" />
                      <div>
                        <div className="h-3.5 w-28 rounded bg-canvas animate-pulse" />
                        <div className="h-2.5 w-36 rounded bg-canvas animate-pulse mt-1.5" />
                      </div>
                    </div>
                    <div className="col-span-2 hidden md:block">
                      <div className="h-3 w-14 rounded bg-canvas animate-pulse" />
                    </div>
                    <div className="col-span-2 hidden lg:block">
                      <div className="h-3 w-24 rounded bg-canvas animate-pulse" />
                    </div>
                    <div className="col-span-2 hidden md:block">
                      <div className="h-3 w-16 rounded bg-canvas animate-pulse" />
                    </div>
                    <div className="col-span-2 hidden lg:block">
                      <div className="h-3 w-20 rounded bg-canvas animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : error ? (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <EmptyState
                icon={<Users size={28} strokeWidth={1.5} />}
                heading="Failed to load clients"
                description="Something went wrong. Please try refreshing the page."
              />
            </div>
          ) : rows.length > 0 ? (
            <div className={`transition-opacity ${isStale ? "opacity-60 pointer-events-none" : ""}`}>
            <div className="bg-paper-hi rounded-lg shadow-card overflow-hidden">
              {/* Table header */}
              <div className="grid grid-cols-12 gap-3 px-5 py-2.5 hair-b bg-wash text-[11px] font-semibold text-ink-4 uppercase tracking-wider">
                <div className="col-span-1 flex items-center">
                  {CheckboxHeader}
                </div>
                <div className="col-span-3 flex items-center gap-1">
                  <span>Name</span>
                </div>
                <div className="col-span-2 hidden md:flex items-center gap-1">
                  <span>Status</span>
                </div>
                <div className="col-span-2 hidden lg:flex items-center gap-1">
                  <span>Last session</span>
                </div>
                <div className="col-span-2 hidden md:flex items-center gap-1">
                  <span>Lifetime</span>
                </div>
                <div className="col-span-2 hidden lg:flex items-center gap-1">
                  <span>Next up</span>
                </div>
              </div>

              {/* Table rows */}
              <div className="divide-hair">
                {rows.map((r) => {
                  const isChecked = checkedIds.has(r.client.id) || selectAllMatching;
                  const highlight = rowHighlight(r);
                  return (
                    <div
                      key={r.client.id}
                      onClick={() => selectClient(r)}
                      className={`grid grid-cols-12 gap-3 px-5 py-3.5 items-center hover:bg-paper-2 transition cursor-pointer ${highlight ?? ""}`}
                    >
                      {/* Checkbox */}
                      <div className="col-span-1 flex items-center">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRowCheck(r.client.id, e.shiftKey);
                          }}
                          className={`w-4 h-4 rounded border hair flex items-center justify-center flex-shrink-0 transition-colors cursor-pointer ${
                            isChecked
                              ? "bg-tide border-tide"
                              : "bg-paper-hi hover:bg-paper-2"
                          }`}
                          aria-label={isChecked ? `Deselect ${r.displayName}` : `Select ${r.displayName}`}
                        >
                          {isChecked && (
                            <Check size={10} strokeWidth={3} className="text-paper-hi" />
                          )}
                        </button>
                      </div>

                      {/* Name */}
                      <div className="col-span-3 min-w-0">
                        <div className="flex items-center gap-3 min-w-0">
                          <Avatar
                            initials={r.initials}
                            size="lg"
                            className={r.isNew ? "bg-tide text-paper-hi" : ""}
                          />
                          <div className="min-w-0">
                            <div className="text-[13.5px] font-medium truncate flex items-center gap-2">
                              {r.displayName}
                              {r.isNew && <Badge variant="spark">New</Badge>}
                              {r.client.archivedAt && (
                                <Badge variant="default">Archived</Badge>
                              )}
                            </div>
                            <div className="text-[11.5px] truncate text-ink-4">
                              {r.secondaryName ?? r.contact?.email ?? "No people yet"}
                            </div>
                            {(r.secondaryName || r.client.peopleCount > 1) && (
                              <div className="text-[11px] truncate text-ink-5">
                                {r.client.peopleCount > 1
                                  ? `${r.client.peopleCount} people`
                                  : r.contact?.email}
                              </div>
                            )}
                            {r.tags.length > 0 && (
                              <div className="mt-1">
                                <TagChips tags={r.tags} />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div className="col-span-2 hidden md:block min-w-0">
                        <ClientStatusPill status={r.status} />
                      </div>

                      {/* Last session */}
                      <div className="col-span-2 hidden lg:block min-w-0">
                        <div className="text-[12.5px] text-ink-3 truncate">
                          {formatDate(r.client.lastSessionAt) ?? "--"}
                        </div>
                      </div>

                      {/* Lifetime spend */}
                      <div className="col-span-2 hidden md:block min-w-0">
                        <div
                          className={`font-display text-[14px] num ${r.client.lifetimeSpend === 0 ? "text-ink-5" : ""}`}
                        >
                          {formatCurrency(r.client.lifetimeSpend)}
                        </div>
                      </div>

                      {/* Next up */}
                      <div className="col-span-2 hidden lg:block min-w-0">
                        <div className="text-[12.5px] text-ink-3 truncate">
                          {formatDate(r.client.nextSessionAt) ?? "--"}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Footer */}
              <div className="px-5 py-3 hair-t bg-wash text-[12px] text-ink-3">
                <Pagination
                  page={page}
                  perPage={PER_PAGE}
                  total={totalCount}
                  onPageChange={setPage}
                  unit="client"
                />
              </div>
            </div>
            </div>
          ) : (
            <div className="bg-paper-hi rounded-lg shadow-card">
              <EmptyState
                icon={<Users size={28} strokeWidth={1.5} />}
                heading={
                  debouncedSearch
                    ? "No clients match your search"
                    : filterKey === "archived"
                      ? "No archived clients"
                      : filterKey === "new"
                        ? "No new clients"
                        : filterKey === "prospects"
                          ? "No prospects"
                          : "No clients yet"
                }
                description={
                  debouncedSearch
                    ? "Try a different name, email, or phone number."
                    : filterKey === "archived"
                      ? "Archived clients will appear here."
                      : filterKey === "new"
                        ? "Clients added in the last 30 days will appear here."
                        : filterKey === "prospects"
                          ? "People who started a booking but haven't completed one yet will appear here."
                          : "Clients will appear here once your first booking is made."
                }
              />
            </div>
          )}
        </div>

        {/* Detail pane */}
        {selectedId && (
          <ClientDetailPane
            ref={detailPaneRef}
            clientId={selectedId}
            onClose={clearSelection}
            listSearch={searchParams.toString()}
          />
        )}
      </div>

      {/* Add client modal */}
      <AddClientModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
      />
    </div>
  );
}
